/**
 * Healing Arcs — the guided ritual sequences in the Awakening tier.
 *
 * Each arc is a 3-session journey with mandatory 72h pacing between sessions.
 * Arc sessions use the facilitate Edge Function with a special seriesContext
 * payload (built in Item #4 — Letters to Others full build).
 *
 * Design constraints:
 *   - Awakening tier only (subscription_tier = 'premium')
 *   - One active arc per user at a time (enforced in DB + client)
 *   - 72h minimum between sessions (enforced from session_N_completed_at timestamps)
 *   - Pre-screen gate before first session (arc-specific questions)
 *   - Burn ritual after sessions 1 and 2; user choice on session 3 (Letters to Others only)
 *   - Session 3 letter is always saved in Letters to Self
 *
 * Attribution (3 placements only, per Marta agreement):
 *   Arc landing screen (full bio), Awakening upgrade page (credential line),
 *   Affirmations footer (subtle). Do NOT add attribution to session pages.
 */

export type ArcId = 'letters_to_others' | 'letters_to_self'
export type ArcStatus = 'in_progress' | 'completed' | 'abandoned'

export interface PreScreenQuestion {
  /** The question text shown to the user */
  text: string
  /**
   * Which answer triggers the warning — 'yes' for most gates,
   * 'no' when the gate asks for a readiness condition.
   */
  warningIf: 'yes' | 'no'
  /** Soft redirect copy shown when the warning fires */
  warningMessage: string
}

export interface ArcSession {
  /** Short title on the session card */
  title: string
  /** "Session N of 3" line */
  subtitle: string
  /** One sentence describing the session's focus */
  description: string
  /**
   * Whether this session ends with the burn ritual.
   * False for the final session of Letters to Others (user's choice)
   * and never burned in Letters to Self session 3 (always saved).
   */
  burnRequired: boolean
  /**
   * Letters to Self session 3 only — always saved to journal.
   * Takes precedence over burnRequired.
   */
  alwaysSaved?: boolean
}

export interface Arc {
  id: ArcId
  name: string
  tagline: string
  description: string
  /** Short attribution line shown in the app — Marta placement 1 of 3 (arc landing) */
  attribution: string
  /** Full bio for the arc landing screen */
  attributionBio: string
  /** Accent color */
  color: string
  /** Soft background color */
  colorSoft: string
  /** Required subscription tier */
  tier: 'awakening'
  /** Always 3 */
  sessionCount: number
  /** Minimum hours between sessions */
  pacingHours: number
  /** Pre-screen gate questions shown before session 1 */
  preScreenQuestions: PreScreenQuestion[]
  /** Session metadata — ordered S1, S2, S3 */
  sessions: ArcSession[]
  /** Shown on the completion screen */
  completionLine: string
  /** False for arcs in the "Coming Soon" state */
  available: boolean
  /** Soft 7-day pacing recommendation label shown between sessions */
  pacingSoftLabel: string
}

export const ARCS: Arc[] = [
  {
    id: 'letters_to_others',
    name: 'Letters to Others',
    tagline: 'Speak the words that were never said.',
    description:
      'A three-session ritual for setting down what you\'ve been carrying about someone who hurt you. '
      + 'Not to excuse them. To reclaim yourself.',
    attribution: 'A ritual by Marta Stankiewicz · Awaken By Love',
    attributionBio:
      'Marta Stankiewicz is a trauma-informed facilitator and founder of Awaken By Love, '
      + 'whose somatic forgiveness practices have guided thousands through the work of self-reclamation. '
      + 'These letters are adapted from her Forgiveness Letter Ritual.',
    color: '#9B72C0',
    colorSoft: 'rgba(155,114,192,0.08)',
    tier: 'awakening',
    sessionCount: 3,
    pacingHours: 72,
    pacingSoftLabel: 'Take at least 3 days. A week if you need it.',
    preScreenQuestions: [
      {
        text: 'Is the harm you\'re carrying still raw and ungrieved — something you haven\'t yet had space to fully feel?',
        warningIf: 'yes',
        warningMessage:
          'This arc works best when there\'s been some distance from the initial wound. '
          + 'A standard session with what you\'re carrying may be the right first step before you begin here.',
      },
      {
        text: 'Is this person still in your life in a way that feels unsafe or unresolved?',
        warningIf: 'yes',
        warningMessage:
          'This arc is about internal release — not reconciliation, contact, or any action toward them. '
          + 'If there\'s active risk or unresolved safety, please work with a therapist before beginning.',
      },
    ],
    sessions: [
      {
        title: 'Truth Untold',
        subtitle: 'Session 1 of 3',
        description: 'What you never got to say. Written here. Burned before you leave.',
        burnRequired: true,
      },
      {
        title: 'The Deeper Layer',
        subtitle: 'Session 2 of 3',
        description: 'What you lost. What you longed for. The grief beneath the anger.',
        burnRequired: true,
      },
      {
        title: 'Release',
        subtitle: 'Session 3 of 3',
        description: 'Not to excuse them. To reclaim yourself.',
        burnRequired: false, // User's choice — save or burn
      },
    ],
    completionLine: 'You set something down here. That matters.',
    available: true,
  },

  {
    id: 'letters_to_self',
    name: 'Letters to Self',
    tagline: 'Return to the one you left behind.',
    description:
      'A three-session journey through self-abandonment and back. '
      + 'For the part of you that has been waiting to be found.',
    attribution: 'A ritual by Marta Stankiewicz · Awaken By Love',
    attributionBio:
      'Marta Stankiewicz is a trauma-informed facilitator and founder of Awaken By Love, '
      + 'whose somatic forgiveness practices have guided thousands through the work of self-reclamation. '
      + 'This arc is adapted from her Returning to Self ritual.',
    color: '#C45272',
    colorSoft: 'rgba(196,82,114,0.08)',
    tier: 'awakening',
    sessionCount: 3,
    pacingHours: 72,
    pacingSoftLabel: 'Take at least 3 days between sessions.',
    preScreenQuestions: [
      {
        text: 'Can you meet yourself with some gentleness today — even imperfectly?',
        warningIf: 'no',
        warningMessage:
          'This arc asks you to witness yourself with care. '
          + 'If that feels impossible right now, a standard session with what you\'re carrying may be more supportive.',
      },
    ],
    sessions: [
      {
        title: 'Voice of Pain',
        subtitle: 'Session 1 of 3',
        description: 'Let the part that was left behind speak. Without editing. Without softening.',
        burnRequired: true,
      },
      {
        title: 'Voice of Understanding',
        subtitle: 'Session 2 of 3',
        description: 'Write back — not to fix it, but to begin to understand.',
        burnRequired: true,
      },
      {
        title: 'The Vow',
        subtitle: 'Session 3 of 3',
        description: 'A promise to stay. To not abandon yourself again.',
        burnRequired: false,
        alwaysSaved: true, // Always saved to journal — never burned
      },
    ],
    completionLine: 'This isn\'t finished. It\'s begun.',
    available: true,
  },
]

/** Look up an arc by id */
export function getArc(id: ArcId): Arc | undefined {
  return ARCS.find(a => a.id === id)
}

/**
 * Returns the minimum timestamp when the next session can begin,
 * given the completion timestamp of the previous session.
 * 72h hard minimum enforced here; softer 7-day recommendation shown separately in UI.
 */
export function nextSessionUnlocksAt(previousSessionCompletedAt: string, pacingHours: number): Date {
  return new Date(new Date(previousSessionCompletedAt).getTime() + pacingHours * 60 * 60 * 1000)
}
